import { Fragment, useEffect, useMemo, useState } from 'react';
import { FileText, Link2, CornerDownRight } from 'lucide-react';
import { useT } from '../../i18n';
import { FilterSelect } from '../../components/FilterSelect';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { SearchableSelect } from '../../components/SearchableSelect';
import { ConfirmModal } from '../../components/ConfirmModal';
import { vjtApi, type TreeMilestone, type TreeTask } from '../vue-jalons-taches/api';
import { docApi, docHref, type AttachedTo, type DocTarget, type RegisterItem } from './api';
import { DocumentsList, DocTranscript, fmtDocDate, transcriptPending } from './DocumentsList';
import { useLiveChanges } from '../../live/live';

// [Proposition: documents] Every document of a project in one table: what it
// is attached to (project › milestone › task › to-do item, or a comment,
// meeting…), who added it and when — comment files included. Documents about
// the whole project (charter, contract…) are added in the block above the
// table and listed first. Any document can be re-attached elsewhere in the
// project ("Rattacher à…") by its author or the PMO — that's how files once
// joined to comments get filed where they belong; it stays visible in its
// comment, and the move is logged in the project history. Documents of
// private tasks the viewer can't open are left out by the server.

// Static keys so the i18n checker can resolve them.
const ATTACHED_TO_KEY: Record<AttachedTo, string> = {
  todo: 'docs.at.todo', task: 'docs.at.task', milestone: 'docs.at.milestone',
  task_comment: 'docs.at.task_comment', project_comment: 'docs.at.project_comment',
  meeting: 'docs.at.meeting', transcription: 'docs.at.transcription', project: 'docs.at.project',
};

/** "Jalon 1 · …", for a cancelled one "Jalon 2 · … (annulé)" (or, deleted before cancellations were kept, "Jalon « … » (annulé)"). */
function registerMilestoneLabel(m: NonNullable<RegisterItem['milestone']>, t: (key: string, params?: Record<string, string | number>) => string): string {
  if (m.cancelled && m.n != null) return t('docs.msCancelledN', { n: m.n, name: m.name || t('milestones.untitled') });
  if (m.cancelled) return m.name ? t('docs.msCancelled', { name: m.name }) : t('docs.msDeleted');
  return t('vjt.ms.title', { n: m.n ?? 0, name: m.name || t('milestones.untitled') });
}

/** "Jalon 1 · … › task › item (to-do item)" — what a register document is attached to. */
export function attachedToLabel(d: RegisterItem, t: (key: string, params?: Record<string, string | number>) => string): string {
  const parts: string[] = [];
  if (d.milestone) parts.push(registerMilestoneLabel(d.milestone, t));
  if (d.task) parts.push(d.task.title);
  if (d.attached_to === 'todo' && d.todo_label) parts.push(d.todo_label);
  const where = parts.join(' › ');
  const how = t(ATTACHED_TO_KEY[d.attached_to]);
  return where ? `${where} (${how})` : how;
}

/** "type:id" select value ↔ target. */
const targetValue = (t: DocTarget) => `${t.type}:${t.id}`;
const parseTargetValue = (v: string): DocTarget => {
  const i = v.indexOf(':');
  return { type: v.slice(0, i) as DocTarget['type'], id: v.slice(i + 1) };
};

/**
 * The register where the follow-up view isn't there to hold it (a project
 * before Execution, on Aperçu): loads the milestones and tasks it needs for
 * "Rattacher à…" itself.
 */
export function ProjectDocumentsRegister({ projectId, reloadKey, onChanged }: {
  projectId: number;
  reloadKey?: unknown;
  onChanged?: () => void;
}) {
  const [tree, setTree] = useState<{ milestones: TreeMilestone[]; tasks: TreeTask[] }>({ milestones: [], tasks: [] });
  useEffect(() => {
    let active = true;
    vjtApi.getTree(projectId)
      .then(d => { if (active) setTree({ milestones: d.milestones, tasks: d.tasks }); })
      .catch(() => { /* the register still works, with "Projet" as the only target */ });
    return () => { active = false; };
  }, [projectId, reloadKey]);
  return <DocumentsRegister projectId={projectId} milestones={tree.milestones} tasks={tree.tasks} reloadKey={reloadKey} onChanged={onChanged} />;
}

export function DocumentsRegister({ projectId, milestones, tasks, reloadKey, onChanged }: {
  projectId: number;
  milestones: TreeMilestone[];
  tasks: TreeTask[];
  reloadKey?: unknown;
  /** After a document was added or re-attached here. */
  onChanged?: () => void;
}) {
  const { t } = useT();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [items, setItems] = useState<RegisterItem[] | null>(null);
  const [milestone, setMilestone] = useState<string>('');
  const [kind, setKind] = useState<string>('');
  const [attaching, setAttaching] = useState<number | null>(null);
  const [pendingAttach, setPendingAttach] = useState<{ doc: RegisterItem; value: string; label: string } | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    docApi.register(projectId).then(setItems).catch(() => setItems([]));
  }, [projectId, reloadKey, reloads]);
  useEffect(() => { setItems(null); }, [projectId]);
  const reload = () => setReloads(n => n + 1);
  const changed = () => { reload(); onChanged?.(); };
  // A transcript under way finishes in the background: pick it up when the server says so.
  useLiveChanges((c) => {
    if (c.area === 'project' && c.projectId && c.projectId !== projectId) return;
    if (items?.some(d => d.transcription && transcriptPending(d.transcription.status))) reload();
  });

  const msTitle = (n: number, name: string) => t('vjt.ms.title', { n, name: name || t('milestones.untitled') });

  // Everything a document can be attached to, in project order.
  const targetOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: targetValue({ type: 'project', id: projectId }), label: t('docs.target.project') }];
    const taskOpts = (list: TreeTask[], prefix: string) => {
      for (const task of list.filter(x => x.can_open)) {
        const taskLabel = prefix ? `${prefix} › ${task.title}` : task.title;
        opts.push({ value: targetValue({ type: 'task', id: task.id }), label: taskLabel });
        for (const todo of task.todos) opts.push({ value: targetValue({ type: 'todo', id: todo.id }), label: `${taskLabel} › ${todo.label}` });
      }
    };
    milestones.forEach((m, i) => {
      if (m.status === 'cancelled') return; // nothing new is attached to a cancelled milestone (it keeps its number)
      const label = msTitle(i + 1, m.name);
      opts.push({ value: targetValue({ type: 'milestone', id: `${projectId}:${m.id}` }), label });
      taskOpts(tasks.filter(x => x.milestone_id === m.id), label);
    });
    const known = new Set(milestones.map(m => m.id));
    taskOpts(tasks.filter(x => !x.milestone_id || !known.has(x.milestone_id)), t('vjt.ms.none'));
    return opts;
  }, [projectId, milestones, tasks, t]);

  const milestoneOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const d of items ?? []) if (d.milestone) seen.set(d.milestone.id, registerMilestoneLabel(d.milestone, t));
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
  }, [items, t]);

  const shown = (items ?? []).filter(d => (!milestone || d.milestone?.id === milestone) && (!kind || d.kind === kind));

  const attachedTo = (d: RegisterItem) => attachedToLabel(d, t);

  // Picking a target only asks for confirmation; the move happens on "Rattacher".
  const attach = (d: RegisterItem, value: string) => {
    const label = targetOptions.find(o => o.value === value)?.label ?? value;
    setPendingAttach({ doc: d, value, label });
  };
  const confirmAttach = () => {
    const p = pendingAttach;
    setPendingAttach(null);
    setAttaching(null);
    if (p) docApi.attach(p.doc.id, parseTargetValue(p.value)).then(changed).catch((e: Error) => alert(e.message));
  };

  return (
    <div className="docs-register" id="docs-register">
      <div className="section-label mb-8 docs-register-title">{t('docs.registerTitle')}{items ? ` (${items.length})` : ''}</div>

      <div className="docs-general">
        <div className="docs-general-title">{t('docs.generalTitle')}</div>
        <DocumentsList target={{ type: 'project', id: projectId }} showHeader={false} onChanged={changed} />
      </div>

      <div className="toolbar mb-8">
        <FilterSelect label={t('filters.milestone')} multiple={false} width={280}
          selected={milestone ? [milestone] : []} options={milestoneOptions}
          onChange={(vals) => setMilestone(vals[0] ?? '')} />
        <FilterSelect label={t('filters.kind')} multiple={false}
          selected={kind ? [kind] : []} options={[{ value: 'file', label: t('docs.kindFile') }, { value: 'link', label: t('docs.kindLink') }]}
          emptyLabel={t('docs.filterAllKinds')} onChange={(vals) => setKind(vals[0] ?? '')} />
      </div>
      {items == null ? <div className="spinner" /> : shown.length === 0 ? (
        <div className="vjt-muted">{t('docs.registerEmpty')}</div>
      ) : (
        <div className="docs-register-table">
          <table className="table">
            <thead>
              <tr>
                <th>{t('docs.colName')}</th>
                <th>{t('docs.colAddedBy')}</th>
                <th>{t('docs.colAddedAt')}</th>
                <th className="docs-attach-col" />
              </tr>
            </thead>
            <tbody>
              {shown.map(d => (
                <Fragment key={d.id}>
                <tr className={attaching === d.id ? 'is-attaching' : undefined}>
                  <td>
                    <a href={docHref(d)} target="_blank" rel="noopener noreferrer" className="docs-name">
                      {d.kind === 'link' ? <Link2 size={12} /> : <FileText size={12} />} {d.name}
                    </a>
                    <DocTranscript doc={{ name: d.name, mime_type: d.mime_type, transcription_id: d.transcription?.id ?? null, transcription_status: d.transcription?.status ?? null }} />
                  </td>
                  <td>{d.added_by}</td>
                  <td className="docs-date">{fmtDocDate(d.added_at)}</td>
                  <td className="docs-attach-col">
                    {(d.added_by_user_id === user?.id || isAdmin) && (
                      <button type="button" className={`btn btn-ghost btn-sm docs-attach-btn${attaching === d.id ? ' active' : ''}`}
                        aria-expanded={attaching === d.id} onClick={() => setAttaching(attaching === d.id ? null : d.id)}>
                        <CornerDownRight size={12} /> {t('docs.attachAction')}
                      </button>
                    )}
                  </td>
                </tr>
                {/* Moving a document: the picker opens on its own line under the row, so the columns stay put. */}
                {attaching === d.id && (
                  <tr className="docs-attach-row">
                    <td colSpan={4}>
                      <div className="docs-attach-pick">
                        <SearchableSelect value="" options={targetOptions} placeholder={t('docs.attachPlaceholder')} onChange={(v) => attach(d, String(v))} />
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAttaching(null)}>{t('common.cancel')}</button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmModal
        open={pendingAttach != null}
        title={t('docs.attachConfirmTitle')}
        message={pendingAttach ? t('docs.attachConfirm', { name: pendingAttach.doc.name, from: attachedTo(pendingAttach.doc), to: pendingAttach.label }) : ''}
        confirmLabel={t('docs.attachConfirmAction')}
        onConfirm={confirmAttach}
        onCancel={() => setPendingAttach(null)}
      />
    </div>
  );
}
